import type { AstrolabeId, LaunchSide, Vec2 } from "@scb/protocol";

/**
 * Astrolabe(星盘) — **부채꼴(반원판)** 득점 구역.
 *
 * 실물 사진(2026-07-27, 라이브 중계 캡처)으로 확인한 형태다: 테이블에 납작하게
 * 놓인 반원 판이고, **호(곡선 가장자리)가 먼 쪽(업슬로프)을 향해 볼록**하다.
 * 호를 따라 작은 페그(말뚝)들이 박혀 있어 페그 사이가 슬롯이 된다.
 * 사진의 판 번호·슬롯 수(7/5/3)·칸 이름(가운데 A, 바깥으로 B·C·D)이
 * 이 구현과 일치한다.
 *
 * 판 몸통은 원판이 아니라 **부채 모양의 띠(고리 조각, annular sector)**다 —
 * 바깥 호(페그·슬롯)와 안쪽 호 사이의 납작한 띠를 옆면 두 개가 닫는다.
 * 실물 근접 사진에서 판이 손부채처럼 납작한 것을 확인했다.
 *
 * ```
 *   먼 쪽 ↑        ↓ 공은 경사를 타고 위에서 내려와 바깥 호의 슬롯에 안착한다
 *          ․B․A․B․        ← 바깥 호 위의 페그(․)와 슬롯 이름
 *        ╭─────────╮      ← 바깥 호 (radius). 페그 사이에 공이 끼어 멈춘다
 *         ╰───────╯       ← 안쪽 호 (innerRadius). 아래에서 오는 공을 막는다
 *   가까운 쪽 ↓   (원의 중심은 판보다 아래(다운슬로프)에 있다)
 *        ↑ 아래에서 올라오는 공은 안쪽 호에 막혀 통과할 수 없다.
 *          판 폭이 테이블보다 좁으므로 **양옆 통로로 돌아** 위로 올라가야 한다.
 * ```
 *
 * **물리 실체가 있는 구조물이다.** 당구대 쿠션처럼 공이 통과할 수 없다.
 * 안착한 공은 고정된 게 아니라 **경사에 밀려 호와 페그에 기대어 있는 상태**다.
 * 나중 공이 때려서 `releaseSpeed`를 넘게 밀면 슬롯에서 다시 굴러 나온다.
 *
 * 슬롯 배치: 판 중심축(+y)을 기준으로 각도 간격 `slotSpacing / radius`(rad)로
 * `slotCount`개가 바깥 호를 따라 늘어서고, 그 사이·양끝에 `slotCount + 1`개의 페그가 있다.
 */
export interface AstrolabeSpec {
  id: AstrolabeId;
  /** 부채꼴 원의 중심. 판 몸통(띠)은 이 점보다 업슬로프에 있다. */
  center: Vec2;
  /** 바깥 호의 반지름 (m). 슬롯·페그가 이 호 위에 있다. */
  radius: number;
  /**
   * 안쪽 호의 반지름 (m). `radius - innerRadius`가 띠의 폭이다.
   * 작을수록 판이 두툼해지고, radius에 가까울수록 얇은 부채가 된다.
   */
  innerRadius: number;
  slotCount: number;
  /** 슬롯 중심 간 간격 — 판 가장자리 호를 따라 잰 호 길이 (m) */
  slotSpacing: number;
  /**
   * 페그(말뚝) 반지름 (m). 페그는 호 바깥으로 살짝 튀어나와 슬롯 사이를 막는다.
   * `slotSpacing - 2·pegRadius > 2·ballRadius` 여야 공이 슬롯에 들어갈 수 있다.
   */
  pegRadius: number;
  /** 슬롯 안착 지점에서 이 반경 안에 들어오면 포획 후보 (m). */
  captureRadius: number;
  /** 이 속도 이하일 때만 안착한다 (m/s). 너무 빠르면 튀어나간다. */
  captureSpeed: number;
  /**
   * 안착한 공이 이 속도를 넘게 밀리면 슬롯에서 빠져나온다 (m/s).
   * `captureSpeed`보다 **훨씬 작아야** 한다 — 공은 captureSpeed보다 느리게 도착해야
   * 안착하므로, 때리러 오는 공도 그만큼 느릴 수밖에 없다. 두 값을 같게 두면
   * 아무리 때려도 빼낼 수 없다. 현재 값에서는 **정확히 맞히면 약 80%** 확률로 빠진다.
   */
  releaseSpeed: number;
}

export interface TableSpec {
  /** 테이블 폭 (m). x축. */
  width: number;
  /** 테이블 길이 (m). y축, 경사 방향. */
  length: number;
  /** Play Line의 y 좌표. 발사는 이 아래(y < playLineY)에서만 가능. */
  playLineY: number;
  ballRadius: number;
  /**
   * 다운슬로프 가속 (m/s²) = g·sinθ. **테이블 전체에 균일하게 작용한다.**
   *
   * 공은 평지에서처럼 그냥 멈추지 않는다 — 뉴턴의 사과처럼 언제나 아래로 당겨진다.
   * 따라서 공이 멈추는 건 **아래쪽에서 뭔가가 받쳐줄 때뿐**이다:
   *   빗의 칸(→ 안착/득점), 빗의 격벽 위(→ Grouping), 이미 멈춘 다른 공(→ Grouping).
   * 그 외에는 전부 경사를 타고 바닥(Gutter)까지 내려간다.
   */
  slopeAccel: number;
  /** 구름 마찰에 의한 감속 (m/s²). `slopeAccel`보다 작아야 공이 되돌아 내려온다. */
  rollingFriction: number;
  /** 공-공 반발계수 */
  ballRestitution: number;
  /** 벽 반발계수 */
  wallRestitution: number;
  /** 스핀 감쇠율 (1/s) */
  spinDecay: number;
  /** 이 속도 이하면 정지로 간주 (m/s) */
  restSpeed: number;
  /**
   * 발사 시 무작위 편차 (0이면 완전 결정론적이고 같은 입력 = 같은 결과).
   * 시드 기반이라 0이 아니어도 리플레이는 재현된다.
   */
  shotJitter: number;
  astrolabes: AstrolabeSpec[];
}

/**
 * ⚠️ 추정치가 섞여 있다. docs/01-game-rules.md "미확정 항목" 참고.
 *
 * 확인된 것: 실물 테이블 4m × 2m, Astrolabe 3개, **부채 모양 띠 판 + 바깥 호를
 *            따라 페그** (실물 사진), 슬롯 수 7/5/3 (만관 보너스 역산 + 사진 확인).
 * 클래식 배치: 세 판 모두 테이블 **가운데 정렬**, 가까운 순으로 1→2→3번 판.
 * 경사는 테이블 전체에 균일하다 — 공은 받쳐지지 않으면 반드시 바닥까지 내려간다.
 * 추정한 것: 각 판의 y 좌표, 판 반지름, 경사각, 마찰·반발계수, 공 지름, 페그 굵기.
 *
 * ⚠️ **폭은 실물(2m)이 아니라 3m다.** 세로형 화면에서 좌우가 너무 좁아 보이고
 * 판 옆 통로가 답답해서 의도적으로 넓혔다. 판 폭(1번 판 지름 0.64m)은 그대로라
 * 양옆 통로가 넓어지고 뱅크샷 각도 여유가 커진다.
 * 실물 규격으로 되돌리려면 width를 2.0으로, astrolabe center.x를 1.0으로 낮추면 된다.
 *
 * 판 반지름은 사진의 비율(판 지름이 테이블 폭의 1/4~1/3, 1번이 가장 크고 3번이
 * 가장 작다)에 맞춰 잡았다. 슬롯 호 간격 0.09는 빗 시절의 칸 간격을 유지한 것 —
 * 페그 사이 입구(0.066)가 공 지름(0.04)보다 넉넉히 넓다.
 */
export const DEFAULT_TABLE: TableSpec = {
  width: 3.0,
  length: 4.0,
  playLineY: 0.35,
  ballRadius: 0.02,
  slopeAccel: 1.6, // ≈ 9.4° 경사
  rollingFriction: 0.25,
  ballRestitution: 0.92,
  wallRestitution: 0.6,
  spinDecay: 2.5,
  restSpeed: 0.02,
  shotJitter: 0,
  astrolabes: [
    {
      id: 1,
      center: { x: 1.5, y: 2.2 },
      radius: 0.32,
      innerRadius: 0.18,
      slotCount: 7,
      slotSpacing: 0.09,
      pegRadius: 0.012,
      captureRadius: 0.024,
      captureSpeed: 1.6,
      releaseSpeed: 0.3,
    },
    {
      id: 2,
      center: { x: 1.5, y: 2.85 },
      radius: 0.24,
      innerRadius: 0.13,
      slotCount: 5,
      slotSpacing: 0.09,
      pegRadius: 0.012,
      captureRadius: 0.024,
      captureSpeed: 1.5,
      releaseSpeed: 0.28,
    },
    {
      id: 3,
      center: { x: 1.5, y: 3.45 },
      radius: 0.16,
      innerRadius: 0.085,
      slotCount: 3,
      slotSpacing: 0.09,
      pegRadius: 0.012,
      captureRadius: 0.024,
      captureSpeed: 1.4,
      releaseSpeed: 0.26,
    },
  ],
};

/** 슬롯 하나가 차지하는 각도 (rad). 호 길이 간격을 반지름으로 나눈 것. */
export function slotArcAngle(spec: AstrolabeSpec): number {
  return spec.slotSpacing / spec.radius;
}

/** 판 중심축(+y) 기준 각도 α 방향의 단위벡터. 양수 = +x 쪽. */
function fanDir(alpha: number): Vec2 {
  return { x: Math.sin(alpha), y: Math.cos(alpha) };
}

/** i번 슬롯의 중심 각도 (rad). 가운데 슬롯이 0(정면). */
export function slotAngle(spec: AstrolabeSpec, i: number): number {
  return (i - (spec.slotCount - 1) / 2) * slotArcAngle(spec);
}

/**
 * i번 슬롯에 안착한 공의 중심 좌표. i는 0부터 slotCount-1.
 * 공은 호에 닿아 멈추므로 중심은 호 바깥 `radius + ballRadius` 거리에 있다.
 */
export function slotCenter(spec: AstrolabeSpec, i: number, ballRadius: number): Vec2 {
  const d = fanDir(slotAngle(spec, i));
  const rest = spec.radius + ballRadius;
  return { x: spec.center.x + d.x * rest, y: spec.center.y + d.y * rest };
}

/**
 * 페그 중심들. 슬롯 사이와 양끝에 `slotCount + 1`개가 있다.
 * i번 슬롯은 i번과 i+1번 페그 사이에 있다.
 * 페그는 호에 박혀 바깥으로 `2·pegRadius`만큼 튀어나온다 (중심이 호에서 pegRadius).
 */
export function pegCenters(spec: AstrolabeSpec): Vec2[] {
  const pegs: Vec2[] = [];
  const arc = slotArcAngle(spec);
  const dist = spec.radius + spec.pegRadius;
  for (let i = 0; i <= spec.slotCount; i++) {
    const d = fanDir((i - spec.slotCount / 2) * arc);
    pegs.push({ x: spec.center.x + d.x * dist, y: spec.center.y + d.y * dist });
  }
  return pegs;
}

export interface Aabb {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** 옆면(직선 모서리) — 안쪽 호 끝과 바깥 호 끝을 잇는 선분 */
export interface FanSide {
  a: Vec2;
  b: Vec2;
}

export interface FanGeometry {
  /** 부채꼴 원 중심 (= spec.center) */
  center: Vec2;
  /** 바깥 호 반지름 (= spec.radius) */
  radius: number;
  /** 안쪽 호 반지름 (= spec.innerRadius) */
  innerRadius: number;
  /** 페그 중심들 (slotCount + 1개) */
  pegs: Vec2[];
  pegRadius: number;
  /** 각 슬롯의 공 안착 지점 */
  slots: Vec2[];
  /** 페그가 늘어선 범위의 절반 각도 (rad). 중심축(+y) 기준. */
  halfAngle: number;
  /** 판 몸통(띠)이 차지하는 범위의 절반 각도 (rad). halfAngle보다 약간 크다. */
  sideAngle: number;
  /** 양 옆면 선분 (왼쪽, 오른쪽) */
  sides: [FanSide, FanSide];
  /** 구조물 전체 범위 (렌더링·브로드페이즈·통로 계산용) */
  band: Aabb;
}

/**
 * 부채꼴 판의 충돌 형상 — 바깥 호와 안쪽 호 사이의 띠 (부채 모양).
 *
 * - 바깥 호 (radius, 업슬로프로 볼록): 위에서 내려온 공이 기대어 멈추는 면
 * - 안쪽 호 (innerRadius): 아래에서 올라오는 공을 막는 면
 * - 옆면: 띠의 양끝을 닫는 직선 모서리
 * - 페그: 바깥 호를 따라 슬롯을 나누는 작은 원기둥들
 *
 * 안착한 공은 슬롯 지점(`slotCenter`)에 놓이고, 그때 바깥 호에 정확히 닿는다.
 */
export function fanGeometry(spec: AstrolabeSpec, ballRadius: number): FanGeometry {
  const reach = spec.radius + spec.pegRadius * 2;
  const slots: Vec2[] = [];
  for (let i = 0; i < spec.slotCount; i++) slots.push(slotCenter(spec, i, ballRadius));

  const halfAngle = (spec.slotCount / 2) * slotArcAngle(spec);
  // 몸통은 양끝 페그보다 살짝 더 나가서 페그가 판 위에 얹혀 보이게 한다.
  const sideAngle = halfAngle + 0.03;

  const side = (sign: 1 | -1): FanSide => {
    const dx = Math.sin(sideAngle) * sign;
    const dy = Math.cos(sideAngle);
    return {
      a: { x: spec.center.x + dx * spec.innerRadius, y: spec.center.y + dy * spec.innerRadius },
      b: { x: spec.center.x + dx * spec.radius, y: spec.center.y + dy * spec.radius },
    };
  };

  return {
    center: spec.center,
    radius: spec.radius,
    innerRadius: spec.innerRadius,
    pegs: pegCenters(spec),
    pegRadius: spec.pegRadius,
    slots,
    halfAngle,
    sideAngle,
    sides: [side(-1), side(1)],
    band: {
      minX: spec.center.x - reach,
      maxX: spec.center.x + reach,
      // 몸통의 가장 아래 지점은 안쪽 호의 양끝이다.
      minY: spec.center.y + spec.innerRadius * Math.cos(sideAngle) - spec.pegRadius,
      maxY: spec.center.y + reach,
    },
  };
}

/** 슬롯 입구 너비 (호 기준). `2 * ballRadius`보다 커야 공이 들어갈 수 있다. */
export function slotMouthWidth(spec: AstrolabeSpec): number {
  return spec.slotSpacing - spec.pegRadius * 2;
}

export function getAstrolabe(table: TableSpec, id: AstrolabeId): AstrolabeSpec {
  const found = table.astrolabes.find((a) => a.id === id);
  if (!found) throw new Error(`astrolabe ${id} not found in table spec`);
  return found;
}

/** 총 슬롯 수 — Grand Slam 판정에 쓰인다. */
export function slotCounts(table: TableSpec): Record<AstrolabeId, number> {
  return {
    1: getAstrolabe(table, 1).slotCount,
    2: getAstrolabe(table, 2).slotCount,
    3: getAstrolabe(table, 3).slotCount,
  };
}

/** 발사 지점이 Play Line 뒤이고 테이블 안인지 검사한다 (Play Line 파울 판정). */
export function isLegalLaunchPoint(table: TableSpec, x: number): boolean {
  return x >= table.ballRadius && x <= table.width - table.ballRadius;
}

/**
 * 발사 지점이 이 자리에서 나온 게 맞는지.
 *
 * 부동소수·표시 반올림 때문에 정확히 같기를 요구하진 않는다. 허용 오차 1cm는
 * 칸 입구(66mm)에 비하면 무시할 만해서, 조작 클라이언트가 이 안에서 흔들어봐야
 * 얻는 게 없다.
 */
export const LAUNCH_POINT_TOLERANCE = 0.01;

/**
 * 자리의 발사 지점 — **한 점으로 고정**이다. 대전에서는 위치를 못 바꾸고
 * 각도·세기·스핀만 조절한다 (실물처럼 자기 자리에 앉아서 친다).
 *
 * 위치는 **옆 통로의 한가운데**로 잡는다: 벽(x=0)과 가장 넓은 빗(1번 판)의
 * 왼쪽 끝 사이 중점. 통로 한복판이라 벽 쪽으로도 빗 쪽으로도 각을 낼 수 있어서
 * 1·2·3번 판을 전부 노릴 수 있다 — 자리를 고정해도 못 넣는 판이 생기지 않는다.
 *
 * ```
 *   0 ─────────●─────────┤▨▨▨▨▨▨▨├───────── width
 *   벽        자리      빗 왼쪽 끝
 *      ← 통로 →
 * ```
 */
export function launchPointFor(table: TableSpec, side: LaunchSide): number {
  return launchPointForSeat(table, side === "left" ? 0 : 1, 2);
}

/** 판 바깥 통로의 폭. 자리는 전부 이 안에 놓인다. */
function corridorEdge(table: TableSpec): number {
  // 판마다 폭이 달라 통로 너비도 다르다. 가장 넓은 판(= 가장 좁은 통로)을 기준
  // 삼아야 세 판 모두에 대해 통로 안에 있는 자리가 된다.
  return Math.min(
    ...table.astrolabes.map((spec) => fanGeometry(spec, table.ballRadius).band.minX),
  );
}

/**
 * `seatCount`명이 앉을 때 `seat`번 자리의 발사 x.
 *
 * 통로는 좌우 두 개뿐이므로 자리를 **번갈아** 배치하고(짝수=왼쪽, 홀수=오른쪽),
 * 같은 통로 안에서는 균등하게 나눈다. 통로를 `[0, 빗 왼쪽 끝]`으로 잡고
 * `(j+1)/(k+1)` 지점에 두면, 2명일 때 정확히 통로 한가운데가 되어
 * 기존 좌/우 자리 좌표(3m 테이블에서 0.5865 / 2.4135)를 그대로 재현한다.
 *
 * ```
 *   0 ──●────●──┤▨▨▨ 빗 ▨▨▨├──●────●── width
 *      s0   s2              s3   s1      (4명일 때)
 * ```
 *
 * 모든 자리는 통로 안이라 어느 자리에서도 1·2·3번 판을 전부 노릴 수 있다.
 */
export function launchPointForSeat(
  table: TableSpec,
  seat: number,
  seatCount: number,
): number {
  const edge = corridorEdge(table);
  const onLeft = seat % 2 === 0;

  // 왼쪽에 앉는 사람 수(짝수 자리)와 오른쪽 사람 수(홀수 자리).
  const leftCount = Math.ceil(seatCount / 2);
  const rightCount = seatCount - leftCount;
  const withinSide = Math.floor(seat / 2);

  if (onLeft) {
    return (edge * (withinSide + 1)) / (leftCount + 1);
  }
  // 오른쪽은 왼쪽 배치를 테이블 중앙 기준으로 뒤집은 것이다 — 좌우 유불리가 없다.
  return table.width - (edge * (withinSide + 1)) / (rightCount + 1);
}

/** 자리 좌표로 쏜 게 맞는지 (좌석 번호 기반). */
export function isLegalLaunchPointForSeat(
  table: TableSpec,
  seat: number,
  seatCount: number,
  x: number,
): boolean {
  return Math.abs(x - launchPointForSeat(table, seat, seatCount)) <= LAUNCH_POINT_TOLERANCE;
}


export function isLegalLaunchPointFor(
  table: TableSpec,
  side: LaunchSide,
  x: number,
): boolean {
  return Math.abs(x - launchPointFor(table, side)) <= LAUNCH_POINT_TOLERANCE;
}
